/**
 * OnboardingLayout — wizard chrome (progress bar, header, footer, exit dialog).
 *
 * The step body is passed via children. Continue/Back/Save state are
 * controlled by the page component so per-step validation can gate them.
 */

import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CircleNotch as Loader2 } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { TriangleLogo } from "@/components/TriangleLogo";
import { useOnboardingStore } from "../store/onboardingStore";
import { STEPS, TOTAL_STEPS } from "../config/steps";

export interface OnboardingLayoutProps {
  /** Render the step body. */
  children: ReactNode;
  /** Disabled state for Continuar (validation gate). */
  canContinue: boolean;
  /** Click handler for Continuar — async returning false aborts step change. */
  onContinue: () => void | Promise<void>;
  /** Show spinner inside Continuar button. */
  isContinuing?: boolean;
  /** Save status badge ("Salvando…" / "Salvo"). */
  saveStatus?: "idle" | "saving" | "saved" | "error";
  /** Human labels of fields still blocking Continuar. Shown as a hint
   *  next to the button so users know exactly what is pending. */
  missingFields?: string[];
}

export function OnboardingLayout({
  children,
  canContinue,
  onContinue,
  isContinuing,
  saveStatus = "idle",
  missingFields,
}: OnboardingLayoutProps) {
  const navigate = useNavigate();
  const [exitOpen, setExitOpen] = useState(false);
  const currentStepIndex = useOnboardingStore((s) => s.currentStepIndex);
  const goPrev = useOnboardingStore((s) => s.goPrev);
  const goNext = useOnboardingStore((s) => s.goNext);
  const { completenessPct } = useBusinessContext();

  const step = STEPS[currentStepIndex];
  const stepNumber = currentStepIndex + 1;
  const progressPct = (stepNumber / TOTAL_STEPS) * 100;
  const isOptionalStep = Boolean(step.optional);
  // After the required steps (Identidade=0, Profissional=1) are passed,
  // expose a "Concluir mais tarde" exit so users aren't trapped in optional
  // steps. Index >= 1 means user already completed Identidade and is on or
  // past Profissional.
  const canFinishLater = currentStepIndex >= 1;

  return (
    <div className="relative min-h-screen bg-background">
      {/* Top progress bar */}
      <div className="fixed inset-x-0 top-0 z-40 h-0.5 bg-muted">
        <motion.div
          className="h-full bg-primary"
          initial={false}
          animate={{ width: `${progressPct}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border/60 bg-background/80 px-5 backdrop-blur md:px-8">
        <div className="flex items-center gap-2">
          <TriangleLogo size="sm" animate />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Código do
            </span>
            <span className="text-sm font-semibold tracking-tight">Poder</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-center text-sm tabular-nums">
          <span className="text-muted-foreground">
            Passo {stepNumber} de {TOTAL_STEPS}
          </span>
          <span aria-hidden className="text-muted-foreground/50">·</span>
          <span className="font-medium">{step.title}</span>
          {completenessPct > 0 ? (
            <span
              className="ml-2 rounded-full border border-border/60 px-2 py-0.5 text-xs text-muted-foreground"
              data-testid="completeness-badge"
            >
              ~{completenessPct}% completo
            </span>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExitOpen(true)}
          data-testid="onboarding-exit-button"
        >
          Sair
        </Button>
      </header>

      {/* Body */}
      <main className="mx-auto flex min-h-[calc(100vh-64px-64px)] w-full max-w-xl flex-col px-6 py-8 md:px-8 md:py-12">
        {children}
      </main>

      {/* Footer */}
      <footer
        className="sticky bottom-0 z-30 border-t border-border/60 bg-background/95 px-5 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 backdrop-blur md:px-8"
        data-testid="onboarding-footer"
      >
        <div className="mx-auto flex max-w-xl items-center justify-between gap-3">
          <div
            className="flex-1 min-w-0 text-xs text-muted-foreground"
            aria-live="polite"
            data-testid="onboarding-footer-status"
          >
            {!canContinue && missingFields && missingFields.length > 0 ? (
              // A12-A11y — semantic warning token (WCAG 1.4.3); hits AA in
              // both light & dark via index.css `--warning`.
              <span className="text-warning">
                Faltam: {missingFields.join(", ")}
              </span>
            ) : saveStatus === "saving" ? (
              "Salvando…"
            ) : saveStatus === "saved" ? (
              "Salvo"
            ) : saveStatus === "error" ? (
              <span className="text-destructive">Falha ao salvar — tente novamente</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={goPrev}
              disabled={currentStepIndex === 0 || isContinuing}
              data-testid="onboarding-back-button"
            >
              Voltar
            </Button>
            {isOptionalStep ? (
              <Button
                variant="ghost"
                onClick={goNext}
                disabled={isContinuing}
                data-testid="onboarding-skip-step-button"
              >
                Pular esta etapa
              </Button>
            ) : null}
            {canFinishLater ? (
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                disabled={isContinuing}
                data-testid="onboarding-finish-later-button"
                className="text-muted-foreground"
              >
                Concluir mais tarde
              </Button>
            ) : null}
            <Button
              onClick={() => void onContinue()}
              disabled={!canContinue || isContinuing}
              data-testid="onboarding-continue-button"
              className="active:scale-[0.98] transition-transform"
            >
              {isContinuing ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Salvando…
                </>
              ) : (
                "Continuar"
              )}
            </Button>
          </div>
        </div>
      </footer>

      <AlertDialog open={exitOpen} onOpenChange={setExitOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair do onboarding?</AlertDialogTitle>
            <AlertDialogDescription>
              Seu progresso está salvo. Você pode continuar de onde parou.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="onboarding-exit-cancel">
              Continuar onboarding
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => navigate("/")}
              data-testid="onboarding-exit-confirm"
            >
              Sair mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
